import { z } from "zod";
import {
  QuestionInputTypeSchema,
  emptyLocalizedText,
  localizedTextSchema,
  requiresOptions,
  type LocalizedText,
  type QuestionInputType,
} from "@modules/rounds/_shared";

/**
 * Form questions as they exist ON THE SERVER.
 *
 * Like documents, these are not part of the wizard's client draft — the panel loads them from
 * `GET /form/` and saves one row at a time.
 */

/** One choice. `value` is what an applicant's answer stores; only the label is translated. */
export const QuestionOptionSchema = z.object({
  id: z.string(),
  value: z.string(),
  label_fr: z.string(),
  label_ar: z.string(),
  label_en: z.string(),
  display_order: z.number().int(),
  is_active: z.boolean(),
});

/**
 * What a round question is FOR — `FOUNDER` or `VENTURE` only, on any NEW write.
 *
 * ⚠ `CONTEXT`/`ELIGIBILITY` are LEGACY-ONLY, kept in the enum purely so a read never throws on an
 * unmigrated row (`RoundFormQuestion.Meta`/backend model comment, DZCH-151, 2026-09-08 —
 * `apps/backend/modules/acquire/models.py`). Migration `0017_normalize_context_question_dimension`
 * already rewrote every existing row to `FOUNDER`/`VENTURE` plus `is_context_question`, so in
 * practice this enum member is now dead weight — but the "strict enums only on values you
 * interpret" lesson (an unrecognised `applicant_step` once threw the ENTIRE form read, see
 * `rules/rounds.md`) says keep tolerating it defensively rather than assume the migration reached
 * every environment. Never branch program logic on `CONTEXT`/`ELIGIBILITY` going forward —
 * `is_context_question`/`is_contextual` is the only correct signal for "is this a context
 * question", axis never was.
 */
export const RoundQuestionAxisSchema = z.enum([
  "FOUNDER",
  "VENTURE",
  "CONTEXT",
  "ELIGIBILITY",
]);

export type RoundQuestionAxis = z.infer<typeof RoundQuestionAxisSchema>;

/**
 * One competency a round question evidences.
 *
 * `code` is the stable identity across framework releases, so it is what a coverage view groups on —
 * the several id fields `serialize_question` also sends all change when the framework is republished.
 */
export const RoundQuestionMappingSchema = z.object({
  code: z.string(),
  side: z.enum(["FOUNDER", "VENTURE"]),
  evidence_order: z.number().int(),
  is_primary: z.boolean(),
});

export type RoundQuestionMapping = z.infer<typeof RoundQuestionMappingSchema>;

/** A question row as `serialize_question` returns it. */
export const QuestionRowSchema = z.object({
  id: z.string(),
  section_id: z.string(),
  /** Set when the row was copied from the bank; the copy is independent from then on. */
  source_bank_question_id: z.string().nullable(),
  question_text_fr: z.string(),
  question_text_ar: z.string(),
  question_text_en: z.string(),
  help_text_fr: z.string(),
  help_text_ar: z.string(),
  help_text_en: z.string(),
  input_type: QuestionInputTypeSchema,
  /**
   * ⚠ SENT ALL ALONG, READ ONLY NOW. `serialize_question` has always returned `axis`, and Zod strips
   * unlisted keys — so the value arrived and was thrown away at the parse boundary. Without it a
   * question cannot be told apart as Founder or Project evidence, which is what decides the section
   * it belongs in and the group it renders under.
   */
  axis: RoundQuestionAxisSchema,
  /**
   * `is_context_question` is the canonical name since DZCH-151 (2026-09-08); `is_contextual` is a
   * read-compatible alias the backend still sends on the SAME response (`serialize_question`
   * returns both, same value — checked directly). Prefer the canonical key so a future removal of
   * the alias costs nothing here; the `.transform` below collapses both into the one field every
   * consumer already reads.
   */
  is_context_question: z.boolean().optional(),
  is_contextual: z.boolean().default(false),
  is_required: z.boolean(),
  display_order: z.number().int(),
  is_active: z.boolean(),
  options: z.array(QuestionOptionSchema),
  /**
   * ⚠ ALSO SENT ALL ALONG. The competencies this question evidences — the whole basis for grouping
   * the builder's question list by competency and for answering "is this competency covered?".
   * Empty for a context or eligibility question, which evidences nothing by definition.
   */
  competency_mappings: z.array(RoundQuestionMappingSchema),
}).transform(({ is_context_question, ...row }) => ({
  ...row,
  is_contextual: is_context_question ?? row.is_contextual,
}));

export type QuestionRow = z.infer<typeof QuestionRowSchema>;

/**
 * `GET /form/` groups questions under sections.
 *
 * ⚠ THE NUMBER OF SECTIONS IS THE BACKEND'S TO CHANGE, AND IT HAS — TWICE. `create_intake_round`
 * loops over `DefaultRoundFormSection`, a table that grows: three rows became seven when the
 * applicant dossier landed (`founder`, `project`, `team`, `practical`, `questions`,
 * `project-questions`, `context-questions`). This comment previously asserted three, and before that
 * one, each time because a reader hardcoded what they happened to see.
 *
 * ⚠ WHICH IS WHY `applicant_step` IS A STRING, NOT AN ENUM. It was
 * `z.enum(["FOUNDER", "PROJECT", "CONTEXT"])`, and the moment the server started sending `TEAM` and
 * `PRACTICAL` the STRICT parse of this whole envelope threw — so `GET /form/` returned nothing, the
 * builder showed every competency group empty, and each one warned that a mandatory competency had
 * no required question. Questions were being created correctly the entire time; only the read was
 * dead.
 *
 * The field is passed through, never branched on. An enum over a value we do not interpret buys no
 * safety and costs the whole panel the first time the backend adds a row to a defaults table it owns.
 */
export const RoundFormSchema = z.object({
  sections: z.array(
    z.object({
      id: z.string(),
      key: z.string(),
      /**
       * The candidat-facing step — `FOUNDER | PROJECT | TEAM | PRACTICAL | CONTEXT` today, and NOT
       * the axis vocabulary (`FOUNDER | VENTURE | CONTEXT | ELIGIBILITY`).
       *
       * Deliberately `z.string()`: see the note above. Nothing here reads it.
       */
      applicant_step: z.string(),
      display_order: z.number().int(),
      is_active: z.boolean(),
      questions: z.array(z.unknown()),
    })
  ),
  config_revision: z.number().int(),
  configuration_frozen: z.boolean().default(false),
});

/** One bank entry. Note `is_required_default` — the bank's suggestion, not a round's setting. */
export const BankQuestionRowSchema = z.object({
  id: z.string(),
  question_text_fr: z.string(),
  question_text_ar: z.string(),
  question_text_en: z.string(),
  input_type: QuestionInputTypeSchema,
  /**
   * ⚠ ALSO SENT AND ALSO IGNORED until now — `GET /question-bank/` returns it per question.
   *
   * Needed because adding a banked question to a round has to name a section, and the section is
   * derived from the axis. Without it the picker cannot tell whether it is handing over a Founder
   * question or a Project one.
   */
  axis: RoundQuestionAxisSchema,
  /**
   * Added directly to `QuestionBankQuestion` by DZCH-151 (2026-09-08) — previously context-ness
   * was inferred from `axis` alone. `QuestionBankDialog`'s table (346:9106) reads this directly now
   * for its own "Axe" column, distinct from `isContextual` passed in for the group the picker was
   * opened under — that one decides where an ADD lands; this one describes the bank row itself.
   */
  is_context_question: z.boolean().optional(),
  is_contextual: z.boolean().default(false),
  is_required_default: z.boolean(),
  options: z.array(QuestionOptionSchema),
  /**
   * The bank's own evidence mappings (`serialize_bank_question`, checked directly) — only `code`
   * and `side` are read: the picker's "Compétence attachée" column resolves the NAME from the
   * round's own catalogue (a bank row carries no localized name of its own), and falls back to an
   * em dash for a context question or one with no mapping at all.
   */
  competency_mappings: z
    .array(z.object({ code: z.string().nullable(), side: z.enum(["FOUNDER", "VENTURE"]).nullable() }))
    .default([]),
}).transform(({ is_context_question, ...row }) => ({
  ...row,
  is_contextual: is_context_question ?? row.is_contextual,
}));

export type BankQuestionRow = z.infer<typeof BankQuestionRowSchema>;

/** One option while being edited: the stable value plus its three labels. */
export const QuestionOptionDraftSchema = z.object({
  value: z.string(),
  label: localizedTextSchema("round-create.errors.option-label-required"),
});

export type QuestionOptionDraft = z.infer<typeof QuestionOptionDraftSchema>;

/** A question while being edited. Validation messages are i18n keys. */
export const QuestionDraftSchema = z
  .object({
    text: localizedTextSchema("round-create.errors.question-text-required"),
    /** Optional guidance shown under the control; blank in every locale is fine. */
    help: z.object({ fr: z.string(), ar: z.string(), en: z.string() }),
    input_type: QuestionInputTypeSchema,
    /**
     * What the question is FOR — and the field that decides which step the candidat meets it in.
     *
     * ⚠ WITHOUT THIS, EVERY QUESTION LANDS IN THE FOUNDER STEP. `QuestionListCreateView` picks the
     * section from the axis when no `section_id` is sent:
     *
     * ```python
     * { CONTEXT: "context-questions", VENTURE: "project-questions" }.get(values.get("axis"), "questions")
     * ```
     *
     * The routing was always correct; we simply never sent an axis, so the lookup got `None` and
     * fell through to the default. A project question then arrives in the middle of questions about
     * the founder, and nothing errors — you would only find it by walking the applicant form.
    */
    axis: RoundQuestionAxisSchema,
    /** Whether this round-local question is shown in its side's Context group. */
    is_contextual: z.boolean(),
    is_required: z.boolean(),
    options: z.array(QuestionOptionDraftSchema),
    /**
     * The competencies this question evidences, by stable `code`.
     *
     * ⚠ WITHOUT THESE A QUESTION COUNTS FOR NOTHING. `QuestionFieldsSerializer` accepts
     * `competency_mappings` on create and `replace_round_question_mappings` writes them — we simply
     * never sent any, so every hand-written question evidenced no competency at all. US-E10-07 then
     * refuses to schedule the round however many questions were written, because no mandatory
     * competency is ever covered.
     *
     * Codes rather than ids, for the usual reason: a competency's id changes with every framework
     * release, its code does not. `toQuestionBody` resolves them at the wire.
     *
     * Empty for a context or eligibility question — the serializer rejects mappings on those
     * outright (`context_question_cannot_map_competency`).
     */
    competency_codes: z.array(z.string()),
  })
  // Mirrors the serializer: a context or eligibility question may not evidence a competency.
  .refine(
    (value) =>
      (!value.is_contextual && value.axis !== "CONTEXT" && value.axis !== "ELIGIBILITY") ||
      value.competency_codes.length === 0,
    {
      message: "round-create.errors.context-question-no-competency",
      path: ["competency_codes"],
    }
  )
  // RADIO / DROPDOWN / MULTI_SELECT require options; every other type — CHECKBOX included —
  // rejects them. Mirrored from the serializer so a bad row never reaches the wire.
  .refine(
    (value) => !requiresOptions(value.input_type) || value.options.length > 0,
    { message: "round-create.errors.options-required", path: ["options"] }
  )
  .refine(
    (value) => requiresOptions(value.input_type) || value.options.length === 0,
    { message: "round-create.errors.options-forbidden", path: ["options"] }
  )
  // The server rejects duplicates too; catching it here keeps the message on the field.
  .refine(
    (value) =>
      new Set(value.options.map((option) => option.value)).size ===
      value.options.length,
    {
      message: "round-create.errors.option-values-duplicate",
      path: ["options"],
    }
  );

export type QuestionDraft = z.infer<typeof QuestionDraftSchema>;

/** Server row -> editable draft. */
export function toQuestionDraft(row: QuestionRow): QuestionDraft {
  return {
    text: {
      fr: row.question_text_fr,
      ar: row.question_text_ar,
      en: row.question_text_en,
    },
    help: { fr: row.help_text_fr, ar: row.help_text_ar, en: row.help_text_en },
    input_type: row.input_type,
    axis: row.axis,
    // `row.is_contextual` alone is correct now: `QuestionRowSchema`'s transform already prefers
    // `is_context_question`, and the axis-based fallback here predates that — every legacy
    // CONTEXT/ELIGIBILITY row was rewritten by migration 0017 with `is_contextual` set true, so
    // checking `axis` too never changes the answer.
    is_contextual: row.is_contextual,
    is_required: row.is_required,
    competency_codes: row.competency_mappings.map((mapping) => mapping.code),
    options: row.options
      .filter((option) => option.is_active)
      .map((option) => ({
        value: option.value,
        label: {
          fr: option.label_fr,
          ar: option.label_ar,
          en: option.label_en,
        },
      })),
  };
}

/** A blank question. SHORT_TEXT because it is the only type valid with no options yet. */
export function emptyQuestionDraft(text: LocalizedText): QuestionDraft {
  return {
    text,
    help: emptyLocalizedText(),
    input_type: "SHORT_TEXT",
    // FOUNDER because it is the section the builder writes into by default; the author changes it
    // when the question is about the project or is context.
    axis: "FOUNDER",
    is_contextual: false,
    is_required: true,
    options: [],
    competency_codes: [],
  };
}

/**
 * Options are meaningless on a type that forbids them, so a type change drops them rather than
 * leaving a hidden payload that the next save would be rejected for.
 */
export function optionsForInputType(
  next: QuestionInputType,
  current: QuestionOptionDraft[]
): QuestionOptionDraft[] {
  return requiresOptions(next) ? current : [];
}

/**
 * Derive an option's stable submitted value from its FRENCH label.
 *
 * French because the value must be locale-independent — an applicant's stored answer cannot
 * depend on which language the form was authored in, and slugging Arabic would leave an opaque
 * value in the export. `index` disambiguates when two labels slug to the same thing, which the
 * server rejects.
 */
export function optionValueFrom(frenchLabel: string, index: number): string {
  const slug = frenchLabel
    .trim()
    .toLocaleLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, "-")
    .replace(/^-|-$/g, "");
  return slug === "" ? `option-${String(index + 1)}` : slug;
}

/**
 * Which section a question of this axis belongs in.
 *
 * Mirrors `QuestionListCreateView`'s own lookup, which is the authority:
 *
 * ```python
 * { CONTEXT: "context-questions", VENTURE: "project-questions" }.get(values.get("axis"), "questions")
 * ```
 *
 * Needed because `POST /questions/from-bank/` REQUIRES `section_id` — unlike create, it has no
 * axis-based fallback, so the caller has to resolve the section itself.
 *
 * ⚠ Two of the four are not a straight rename: VENTURE becomes PROJECT, and ELIGIBILITY collapses
 * into CONTEXT. That is the part that goes silently wrong.
 */
export function sectionKeyForAxis(axis: RoundQuestionAxis): string {
  switch (axis) {
    case "VENTURE":
      return "project-questions";
    case "CONTEXT":
    case "ELIGIBILITY":
      return "context-questions";
    case "FOUNDER":
      return "questions";
  }
}

// ── The builder's question list: grouping, coverage, filtering ───────────────────────────────

/**
 * One group of the builder's left panel: a competency and the questions evidencing it.
 *
 * `code` is null for the « Contexte » group, which is not a competency but the informational
 * questions that evidence none (DZCH-132). Given the same shape as a competency group so it is
 * modelled and rendered the same way, as a peer section — the one difference being that it is
 * ALWAYS present, unlike a competency group which only exists for a SELECTED competency.
 */
export interface QuestionGroup {
  kind: "COMPETENCY" | "CONTEXT" | "UNASSIGNED";
  /** Stable UI identity; unlike `code`, this also distinguishes the two Context groups. */
  key: string;
  code: string | null;
  name: string;
  side: "FOUNDER" | "VENTURE" | null;
  isMandatory: boolean;
  questions: PanelQuestion[];
  /**
   * Whether this group counts as COVERED.
   *
   * "Has at least one REQUIRED mapped question." ⚠ Stricter than the backend's own
   * `competency_coverage` readiness check (`services.py`), which only asks for ANY active mapped
   * question and does not look at `is_required` — a competency evidenced only by optional
   * questions currently passes the server's own gate and can be scheduled. Akram's explicit call
   * 2026-09-10: an optional-only competency is real content risk (the applicant may leave every
   * one of them blank, so the round could open with a "covered" competency that ends up with zero
   * answers), so the builder blocks it here even though the server would still accept it — a
   * deliberate, acknowledged departure from "readiness is the server's, never re-derived", not an
   * oversight. Flagged to Aymen as a real backend gap (see `competencies-bank-backend-asks`
   * sibling memory / round-builder-backend-asks).
   */
  isCovered: boolean;
}

interface GroupInput {
  questions: readonly PanelQuestion[];
  /** The round's SELECTED competencies, in the order the side panel lists them. */
  competencies: readonly {
    code: string;
    name: string;
    side: "FOUNDER" | "VENTURE";
    isMandatory: boolean;
  }[];
}

/**
 * Group the round's questions by the competency each evidences.
 *
 * A question evidencing SEVERAL competencies appears under EACH — that is the point of the view, not
 * a duplication bug. The mapping is many-to-many, and a question that genuinely evidences two
 * competencies covers both.
 *
 * Groups follow the COMPETENCY order rather than the question order, so the panel reads in the same
 * sequence as the selection panel beside it. The no-competency group is always last.
 */
export function groupQuestionsByCompetency({
  questions,
  competencies,
}: GroupInput): QuestionGroup[] {
  const groups: QuestionGroup[] = competencies.map((competency) => {
    const matching = questions.filter((question) =>
      question.competencyCodes.includes(competency.code)
    );
    return {
      kind: "COMPETENCY",
      key: `competency:${competency.code}`,
      code: competency.code,
      name: competency.name,
      side: competency.side,
      isMandatory: competency.isMandatory,
      questions: matching,
      isCovered: matching.some((question) => question.is_required),
    };
  });

  const contextual = questions.filter((question) => question.is_contextual);
  const unmapped = questions.filter(
    (question) => !question.is_contextual && question.competencyCodes.length === 0
  );

  const contextGroups: QuestionGroup[] = (["FOUNDER", "VENTURE"] as const).map(
    (side) => ({
      kind: "CONTEXT",
      key: `context:${side}`,
      code: null,
      name: "",
      side,
      isMandatory: false,
      // `axis` is FOUNDER/VENTURE only post-migration (see `RoundQuestionAxisSchema`'s doc
      // comment) — a straight equality check, no legacy branch needed.
      questions: contextual.filter((question) => question.axis === side),
      isCovered: true,
    })
  );

  const unassignedGroups: QuestionGroup[] = (["FOUNDER", "VENTURE"] as const)
    .map((side) => ({
      kind: "UNASSIGNED" as const,
      key: `unassigned:${side}`,
      code: null,
      name: "",
      side,
      isMandatory: false,
      questions: unmapped.filter((question) => question.axis === side),
      isCovered: true,
    }))
    .filter((group) => group.questions.length > 0);

  // ⚠ ALWAYS PRESENT, even with zero questions (DZCH-132). Every SELECTED competency group above
  // renders unconditionally, ready for its first question — this one used to appear only once a
  // Context question already existed, which meant there was no "Context" section to press
  // « Écrire une question » / « Ajouter depuis la banque » in until one somehow existed already. A
  // permanent group is what makes both actions reachable for the FIRST context question.
  return [...groups, ...contextGroups, ...unassignedGroups];
}

/**
 * Selected competencies missing a REQUIRED mapped question — what the builder itself blocks on
 * (see `isCovered`'s doc comment for why this is stricter than the server's own readiness check).
 *
 * ⚠ NOT SCOPED TO MANDATORY COMPETENCIES (DZCH-130). "Optional" only controls whether the applicant
 * may leave the answer blank — it never exempts a SELECTED competency from needing a question at
 * all. This used to filter on `group.isMandatory`, so an optional competency selected with zero
 * questions mapped to it was silently accepted: nothing here warned, and the summary line above the
 * list didn't count it. Every selected competency is covered here for the same reason.
 */
export function uncoveredGroups(
  groups: readonly QuestionGroup[]
): QuestionGroup[] {
  return groups.filter((group) => group.code !== null && !group.isCovered);
}

/**
 * The four filters the design offers (Figma 331:26105).
 *
 * All CLIENT-side: `GET /form/` returns every question in one response, so there is nothing to ask
 * the server for and no reason to re-fetch on a filter change. `null` means "not filtered".
 */
export interface QuestionFilters {
  /** « Tous les côtés » — Porteur or Projet. */
  side: "FOUNDER" | "VENTURE" | null;
  /** « Toute couverture » — filters GROUPS, not questions. */
  coverage: "covered" | "uncovered" | null;
  /** « Requise ou optionnelle ». */
  requirement: "required" | "optional" | null;
  /** « Tous les types de réponse ». */
  inputType: QuestionInputType | null;
}

export const NO_QUESTION_FILTERS: QuestionFilters = {
  side: null,
  coverage: null,
  requirement: null,
  inputType: null,
};

export function hasActiveFilters(filters: QuestionFilters): boolean {
  return (
    filters.side !== null ||
    filters.coverage !== null ||
    filters.requirement !== null ||
    filters.inputType !== null
  );
}

/**
 * Apply the filters to the grouped list.
 *
 * ⚠ TWO DIFFERENT SCOPES, deliberately. `side` and `coverage` filter GROUPS — they are properties of
 * a competency, so hiding a matching question but keeping its heading would be nonsense.
 * `requirement` and `inputType` filter QUESTIONS within the groups that survive.
 *
 * A group emptied by a question-level filter is KEPT, not dropped: its heading and its "add a
 * question" actions are the point of the panel, and hiding a competency because a type filter
 * matched none of its questions would read as the competency having been removed.
 */
export function filterQuestionGroups(
  groups: readonly QuestionGroup[],
  filters: QuestionFilters
): QuestionGroup[] {
  return groups
    .filter((group) => {
      // The no-competency group has no side and no coverage, so either of those filters excludes it.
      if (filters.side !== null && group.side !== filters.side) return false;
      if (filters.coverage === "covered" && !group.isCovered) return false;
      if (filters.coverage === "uncovered") {
        if (group.code === null || group.isCovered) return false;
      }
      return true;
    })
    .map((group) => ({
      ...group,
      questions: group.questions.filter((question) => {
        if (filters.requirement === "required" && !question.is_required) {
          return false;
        }
        if (filters.requirement === "optional" && question.is_required) {
          return false;
        }
        if (
          filters.inputType !== null &&
          question.input_type !== filters.inputType
        ) {
          return false;
        }
        return true;
      }),
    }));
}

// ── Questions held in the CLIENT draft ──────────────────────────────────────────────────────

/**
 * A question composed before the round exists.
 *
 * ⚠ WHY THIS EXISTS. Round questions are server state — `POST /questions/` needs a round — and step
 * 1 no longer creates one, because `POST /intake-rounds/` refuses until the approved reference and
 * legal manifests are installed. Without a local form the whole left half of step 3 was a sentence
 * explaining why it was empty.
 *
 * Same trade as documents and the competency selection: held in the draft, persisted across a
 * reload, and pushed on creation.
 */
export const DraftQuestionSchema = QuestionDraftSchema.safeExtend({
  /**
   * A client-side identity, because there is no server id yet.
   *
   * Needed for React keys, for the editing-open state, and for edit/remove to name a row. Never
   * sent: the server assigns the real id at creation.
   */
  local_id: z.string(),
  /** The bank entry this was copied from, when it came from the bank rather than being written. */
  from_bank_id: z.string().nullable(),
});

export type DraftQuestion = z.infer<typeof DraftQuestionSchema>;

/** A fresh local id. `crypto.randomUUID` is available in every browser the platform targets. */
export function newDraftQuestionId(): string {
  return crypto.randomUUID();
}

/**
 * One question as the PANEL renders it, from either source.
 *
 * ⚠ A VIEW TYPE, deliberately — not a fake server row. A draft question has no `section_id`, no
 * `display_order` and no server `id`, so synthesising one would mean inventing three fields the
 * panel then could not trust. This carries only what the list actually shows plus the codes it
 * groups on, and `isDraft` is what tells the step which save path a row belongs to.
 */
export interface PanelQuestion {
  /** Server id, or the draft's `local_id`. Unique within one panel either way. */
  id: string;
  text: LocalizedText;
  input_type: QuestionInputType;
  axis: RoundQuestionAxis;
  is_contextual: boolean;
  is_required: boolean;
  competencyCodes: string[];
  isFromBank: boolean;
  /** True when this row lives in the client draft and has never been saved. */
  isDraft: boolean;
}

/** A saved round question, as the panel sees it. */
export function toPanelQuestion(row: QuestionRow): PanelQuestion {
  return {
    id: row.id,
    text: {
      fr: row.question_text_fr,
      ar: row.question_text_ar,
      en: row.question_text_en,
    },
    input_type: row.input_type,
    axis: row.axis,
    // See the matching note in `toQuestionDraft` — the axis fallback is redundant now.
    is_contextual: row.is_contextual,
    is_required: row.is_required,
    competencyCodes: row.competency_mappings.map((mapping) => mapping.code),
    isFromBank: row.source_bank_question_id !== null,
    isDraft: false,
  };
}

/** A draft question, as the panel sees it. */
export function draftToPanelQuestion(draft: DraftQuestion): PanelQuestion {
  return {
    id: draft.local_id,
    text: draft.text,
    input_type: draft.input_type,
    axis: draft.axis,
    is_contextual: draft.is_contextual,
    is_required: draft.is_required,
    competencyCodes: draft.competency_codes,
    isFromBank: draft.from_bank_id !== null,
    isDraft: true,
  };
}
